import { resolveProofApiUrl } from './proof-api-origin';
import { z } from 'zod';
import {
  proofLibraryResponseSchema,
  proofLibraryRowSchema,
  proofNavigationCountsResponseSchema,
  type ProofLibraryRow,
  type ProofNavigationCounts,
} from '@/lib/proof-library/library-contract';
import type { ProofLibraryCreateInput, ProofLibraryWriteInput } from '@/lib/proof-library/write-contract';

const proofLibraryRowResponseSchema = z.object({ ok: z.literal(true), row: proofLibraryRowSchema });
import type {
  ProofMetric,
  ProofMetricInsert,
  ProofMetricListFilters,
  ProofMetricUpdate,
  StoredPublicProofPayload,
} from '@/types/customer-ops';

type ProofRowsResponse = {
  ok: true;
  rows: ProofMetric[];
};

/** `approved` is an alias of `ready` during the status transition. */
function expandStatusFilter(status: ProofMetricListFilters['status']): string[] {
  const list = Array.isArray(status) ? status : status ? [status] : [];
  const expanded = new Set<string>(list);
  if (expanded.has('approved') || expanded.has('ready')) {
    expanded.add('approved');
    expanded.add('ready');
  }
  return [...expanded];
}

async function accessToken(): Promise<string> {
  const { supabase } = await import('@/lib/supabase/client');
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  if (error || !session?.access_token) {
    throw new Error('Your admin session expired. Sign in again.');
  }
  return session.access_token;
}

async function proofApi<T>(path: string, init?: RequestInit): Promise<T> {
  const requestUrl = resolveProofApiUrl(
    `/api/proof-metrics${path}`,
    import.meta.env.VITE_API_URL || '',
    window.location.origin,
  );
  const token = await accessToken();
  const response = await fetch(requestUrl, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const message = payload?.error?.message || 'Proof library request failed.';
    throw new Error(message);
  }
  return payload as T;
}

function filtersQuery(filters?: ProofMetricListFilters): string {
  const params = new URLSearchParams();
  if (filters?.status) {
    params.set('status', (Array.isArray(filters.status) ? filters.status : [filters.status]).join(','));
  }
  if (filters?.locationId) params.set('locationId', filters.locationId);
  if (filters?.proofMetricId) params.set('proofMetricId', filters.proofMetricId);
  const query = params.toString();
  return query ? `&${query}` : '';
}

function withoutOk<T extends { ok: true }>(payload: T): Omit<T, 'ok'> {
  const { ok: _ok, ...result } = payload;
  return result;
}

export const proofMetricsService = {
  async list(filters?: ProofMetricListFilters): Promise<ProofMetric[]> {
    const response = await proofApi<ProofRowsResponse>(`?view=approved${filtersQuery(filters)}`);
    return response.rows;
  },

  async navigationCounts(): Promise<ProofNavigationCounts> {
    const payload = proofNavigationCountsResponseSchema.parse(
      await proofApi<unknown>('?view=navigation_counts'),
    );
    return withoutOk(payload);
  },

  /** Every library row up to the server cap; the page searches, filters and sorts in the browser. */
  async library(): Promise<{ rows: ProofLibraryRow[]; total: number; omitted: number }> {
    const payload = proofLibraryResponseSchema.parse(await proofApi<unknown>('?view=library'));
    return { rows: payload.rows, total: payload.total, omitted: payload.omitted };
  },

  /** "Type it in": saves a ready row from the ~15-field form. */
  async libraryCreate(data: ProofLibraryCreateInput): Promise<ProofLibraryRow> {
    const payload = proofLibraryRowResponseSchema.parse(
      await proofApi<unknown>('', { method: 'POST', body: JSON.stringify({ action: 'library_create', data }) }),
    );
    return payload.row;
  },

  /** Inline edit: saves the ~15 fields in place; the stored status is preserved. */
  async libraryUpdate(id: string, data: ProofLibraryWriteInput): Promise<ProofLibraryRow> {
    const payload = proofLibraryRowResponseSchema.parse(
      await proofApi<unknown>('', { method: 'POST', body: JSON.stringify({ action: 'library_update', id, data }) }),
    );
    return payload.row;
  },

  async libraryArchive(id: string, reason = ''): Promise<ProofLibraryRow> {
    const payload = proofLibraryRowResponseSchema.parse(
      await proofApi<unknown>('', { method: 'POST', body: JSON.stringify({ action: 'library_archive', id, data: { reason } }) }),
    );
    return payload.row;
  },

  async libraryRestore(id: string): Promise<ProofLibraryRow> {
    const payload = proofLibraryRowResponseSchema.parse(
      await proofApi<unknown>('', { method: 'POST', body: JSON.stringify({ action: 'library_restore', id }) }),
    );
    return payload.row;
  },
};

// The browser service above uses the authenticated admin API. This injected
// service is deliberately separate so the public proof endpoint can read only
// approved source rows with a server-side Supabase client.
export interface ProofMetricsSupabaseClient {
  from(table: string): any;
}

type ProofMetricsClientSource =
  | ProofMetricsSupabaseClient
  | (() => Promise<ProofMetricsSupabaseClient>);

async function defaultPublicProofClient(): Promise<ProofMetricsSupabaseClient> {
  const { supabase: client } = await import('@/lib/supabase/client');
  return client;
}

async function resolvePublicProofClient(source: ProofMetricsClientSource): Promise<ProofMetricsSupabaseClient> {
  return typeof source === 'function' ? source() : source;
}

function normalizeTextList(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    const identity = trimmed.toLocaleLowerCase();
    if (!trimmed || seen.has(identity)) continue;
    seen.add(identity);
    normalized.push(trimmed);
  }
  return normalized;
}

function publicProofPayload(payload: StoredPublicProofPayload | undefined): StoredPublicProofPayload {
  return payload && Object.keys(payload).length > 0 ? { ...payload } as StoredPublicProofPayload : {};
}

function applyDirectFilters(query: any, filters?: ProofMetricListFilters) {
  let next = query;
  if (!filters) return next;
  if (filters.status) next = next.in('status', expandStatusFilter(filters.status));
  if (filters.proofMetricId) next = next.eq('proof_metric_id', filters.proofMetricId);
  if (filters.locationId) next = next.overlaps('location_ids', [filters.locationId]);
  if (filters.channel) next = next.contains('channels_allowed', [filters.channel]);
  if (filters.publicSlot) next = next.eq('public_slot', filters.publicSlot);
  return next;
}

/** An update clears any existing public-website approval whenever it touches something the approval depended on. */
function shouldClearPublicApprovalForUpdate(payload: ProofMetricUpdate): boolean {
  return (
    payload.public_slot === null ||
    payload.public_approved_at === null ||
    (payload.public_approved_by !== undefined && payload.public_approved_by.every((name) => !name.trim())) ||
    (payload.public_approval_basis_ref !== undefined && !payload.public_approval_basis_ref?.trim()) ||
    (payload.channels_allowed !== undefined && !payload.channels_allowed.includes('public_website'))
  );
}

export function createProofMetricsService(
  clientSource: ProofMetricsClientSource = defaultPublicProofClient,
) {
  async function client() {
    return resolvePublicProofClient(clientSource);
  }

  return {
    async list(filters?: ProofMetricListFilters): Promise<ProofMetric[]> {
      try {
        const source = await client();
        const query = applyDirectFilters(source.from('proof_metrics').select('*').order('updated_at', { ascending: false }), filters);
        const { data, error } = await query;
        if (error) {
          console.error('Error fetching proof metrics:', error);
          return [];
        }
        return (data as ProofMetric[]) ?? [];
      } catch (error) {
        console.error('Error fetching proof metrics:', error);
        return [];
      }
    },

    async listStrict(filters?: ProofMetricListFilters): Promise<ProofMetric[]> {
      try {
        const source = await client();
        const query = applyDirectFilters(source.from('proof_metrics').select('*').order('updated_at', { ascending: false }), filters);
        const { data, error } = await query;
        if (error) throw new Error('source unavailable');
        return (data as ProofMetric[]) ?? [];
      } catch {
        throw new Error('Proof metrics unavailable.');
      }
    },

    async getById(id: string): Promise<ProofMetric | null> {
      const source = await client();
      const { data, error } = await source.from('proof_metrics').select('*').eq('id', id).single();
      if (error?.code === 'PGRST116') return null;
      if (error) throw new Error(`Failed to fetch proof metric: ${error.message}`);
      return (data as ProofMetric) ?? null;
    },

    async getByProofMetricId(proofMetricId: string): Promise<ProofMetric | null> {
      const source = await client();
      const { data, error } = await source
        .from('proof_metrics')
        .select('*')
        .eq('proof_metric_id', proofMetricId)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(`Failed to fetch proof metric: ${error.message}`);
      return (data as ProofMetric) ?? null;
    },

    async create(input: ProofMetricInsert): Promise<ProofMetric> {
      const source = await client();
      const payload = {
        ...input,
        source_ref: input.source_ref.trim(),
        public_slot: input.public_slot ?? null,
        public_payload: publicProofPayload(input.public_payload),
        public_approved_by: normalizeTextList(input.public_approved_by ?? []),
        public_approved_at: input.public_approved_at ?? null,
        public_approval_basis_ref: input.public_approval_basis_ref?.trim() || null,
      };
      const { data, error } = await source.from('proof_metrics').insert(payload).select('*').single();
      if (error) throw new Error(`Failed to create proof metric: ${error.message}`);
      return data as ProofMetric;
    },

    async update(id: string, input: ProofMetricUpdate): Promise<ProofMetric> {
      const source = await client();
      const payload: Record<string, unknown> = { ...input };
      if (input.channels_allowed) payload.channels_allowed = normalizeTextList(input.channels_allowed);
      if (input.public_approved_by) payload.public_approved_by = normalizeTextList(input.public_approved_by);
      if (input.public_payload !== undefined) payload.public_payload = publicProofPayload(input.public_payload);
      if (input.public_approval_basis_ref !== undefined) {
        payload.public_approval_basis_ref = input.public_approval_basis_ref?.trim() || null;
      }
      if (shouldClearPublicApprovalForUpdate(input)) {
        let channels = payload.channels_allowed as string[] | undefined;
        if (!channels) {
          const current = await source.from('proof_metrics').select('channels_allowed').eq('id', id).single();
          if (current.error) throw new Error(`Failed to clear public approval: ${current.error.message}`);
          channels = Array.isArray(current.data?.channels_allowed) ? current.data.channels_allowed : [];
        }
        payload.channels_allowed = (channels ?? []).filter((channel) => channel !== 'public_website');
        payload.public_approved_by = [];
        payload.public_approved_at = null;
        payload.public_approval_basis_ref = null;
      }
      const { data, error } = await source.from('proof_metrics').update(payload).eq('id', id).select('*').single();
      if (error) throw new Error(`Failed to update proof metric: ${error.message}`);
      return data as ProofMetric;
    },
  };
}
